import axios from "axios";
import { ActionContext, Module } from "vuex";

import { Code, Evidence, Indexed } from "@/types";
import { auth as manager } from "@/auth";
import { analytics } from "@/analytics";
import { RootState } from "@/store/root";
import {
  AddCommand,
  Command,
  MutateCommand,
  RemoveCommand,
  ReplaceCommand,
  ReplaceExistingCommand
} from "@/commands";

export interface EvidencesState {
  inspected: Set<string>;
  list: Evidence[];
  code?: string;
}

interface RawEvidence {
  code: string;
  codename: string;
  tokspans: any[];
}

const compare = (first: Evidence, second: Evidence) => {
  if (first.spans.length === 0) return -1;
  if (second.spans.length === 0) return 1;
  const [a, b] = [first.spans[0][0], second.spans[0][0]];
  return a > b ? 1 : a < b ? -1 : 0;
};

const execute = <R, S>(context: ActionContext<R, S>, command: Command) => {
  command
    .execute()
    .forEach(([type, payload]) =>
      context.commit(type, payload, { root: true })
    );

  context.commit("history/PUSH", command, { root: true });
};

export const evidences: Module<EvidencesState, RootState> = {
  state: {
    inspected: new Set<string>(),
    list: []
  },
  namespaced: true,
  mutations: {
    UPDATE(state: EvidencesState, value: Evidence[]) {
      state.list = value;
    },
    ADD(state: EvidencesState, { index, value: evidence }: Indexed<Evidence>) {
      state.list.splice(index, 0, evidence);
    },
    REMOVE(state: EvidencesState, index: number) {
      if (state.inspected.has(state.list[index].id)) {
        state.inspected.delete(state.list[index].id);
      }

      state.list.splice(index, 1);
    },
    INSPECT(state: EvidencesState, evidence: Evidence) {
      state.inspected.add(evidence.id);
    },
    CLEAR(state: EvidencesState) {
      state.inspected = new Set<string>();
      state.list = [];
    }
  },
  actions: {
    async update(context: ActionContext<EvidencesState, RootState>) {
      if (analytics.enabled) {
        analytics.record({ name: "encode" });
      }

      try {
        context.commit("UPDATE_STATUS", "loading", { root: true });

        // eslint-disable-next-line no-control-regex
        const text = context.rootState.raw.replace(/[^\x00-\x7F]/g, "");

        await context.dispatch("matches/update", false, { root: true });

        const response = await axios.post(
          (context.rootState as any).settings.current.endpoints.encode,
          text,
          {
            headers: manager.enabled ? await manager.headers() : {}
          }
        );

        const evidences = response.data.icd.imatches
          .filter((match: any) => Object.keys(match).length)
          .map((evidence: RawEvidence) => ({
            id: evidence.code,
            desc: evidence.codename,
            spans: evidence.tokspans.filter(span => span.length),
            billable: true
          }));

        context.commit("UPDATE_TIME", response.data.computetime, {
          root: true
        });

        context.commit(
          "UPDATE",
          evidences
            .filter(
              (evidence: Evidence) =>
                evidence.spans && evidence.spans.length > 0
            )
            .sort(compare)
        );

        context.commit("UPDATE_STATUS", "idle", { root: true });
      } catch (e) {
        context.commit("UPDATE_TIME", undefined, { root: true });
        context.commit("UPDATE_STATUS", "failed", { root: true });
      }
    },
    async remove(
      context: ActionContext<EvidencesState, RootState>,
      code: string
    ) {
      const index = context.state.list.findIndex(e => e.id === code);

      if (index >= 0) {
        execute(
          context,
          new RemoveCommand({
            index,
            value: context.state.list[index]
          })
        );
      }
    },
    add(context: ActionContext<EvidencesState, RootState>, evidence: Evidence) {
      execute(
        context,
        new AddCommand({
          index: context.state.list.length,
          value: evidence
        })
      );
    },
    mutate(
      context: ActionContext<EvidencesState, RootState>,
      list: Evidence[]
    ) {
      execute(context, new MutateCommand(context.state.list, list));
    },
    clear(context: ActionContext<EvidencesState, RootState>) {
      context.commit("CLEAR");
    },
    replace(
      context: ActionContext<EvidencesState, RootState>,
      params: { original: string; new: Code }
    ) {
      if (params.original === params.new.id) return;

      const indexes = {
        previous: context.state.list.findIndex(
          evidence => evidence.id === params.original
        ),
        next: context.state.list.findIndex(
          evidence => evidence.id === params.new.id
        )
      };

      execute(
        context,
        indexes.next < 0
          ? new ReplaceCommand(
              indexes.previous,
              context.state.list[indexes.previous],
              {
                id: params.new.id,
                desc: params.new.desc,
                spans: context.state.list[indexes.previous].spans,
                billable: params.new.billable,
                type: "suggested"
              }
            )
          : new ReplaceExistingCommand(
              {
                index: indexes.previous,
                value: context.state.list[indexes.previous]
              },
              { index: indexes.next, value: context.state.list[indexes.next] }
            )
      );
    }
  },
  getters: {
    indexed(state: EvidencesState) {
      return state.list.reduce(
        (result: { [key: string]: Evidence }, evidence: Evidence) => ({
          ...result,
          [evidence.id]: evidence
        }),
        {}
      );
    },
    sorted(state: EvidencesState) {
      return [...state.list].sort(compare);
    },
    parsed(state: EvidencesState, _, rootState: RootState, rootGetters: any) {
      if (!rootState.tokens) return [];

      const result: Evidence[][] = Array.from(
        { length: rootState.tokens.length },
        () => []
      );

      for (let code of state.list) {
        for (let span of code.spans) {
          const upper = Math.min(span[1], rootState.tokens.length);
          for (let i = span[0]; i < upper; i++) {
            if (i in result) result[i].push(code);
          }
        }
      }

      return result;
    }
  }
};
